import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useQuery } from "react-query";
import styled from "styled-components";
import { motion } from "framer-motion";
import {
  FiSearch,
  FiFilter,
  FiMapPin,
  FiTruck,
  FiArrowRight,
  FiCalendar,
  FiDollarSign,
} from "react-icons/fi";
import { mealsAPI } from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";
import LoadingSpinner from "../../components/LoadingSpinner";
import toast from "react-hot-toast";

const MealsContainer = styled.div`
  min-height: 100vh;
  background: #f8f9fa;
  padding: 2rem 0;
`;

const MealsContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;

  @media (max-width: 768px) {
    padding: 0 0.75rem;
  }

  @media (max-width: 480px) {
    padding: 0 0.5rem;
  }
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;

  h1 {
    font-size: 2.5rem;
    font-weight: 700;
    color: #2d5016;
    margin-bottom: 0.5rem;
  }

  p {
    color: #666;
    font-size: 1.125rem;
  }
`;

const SearchAndFilters = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    padding: 1rem;
    margin-bottom: 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 0.75rem;
    margin-bottom: 1rem;
  }
`;

const FiltersRow = styled.div`
  display: flex;
  gap: 1rem;
  align-items: end;
  flex-wrap: wrap;
`;

const FilterSelect = styled.select`
  padding: 0.75rem;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 0.875rem;
  background: white;
  cursor: pointer;
  transition: border-color 0.3s ease;
  min-width: 150px;

  &:focus {
    outline: none;
    border-color: #2d5016;
  }
`;

const SearchBar = styled.div`
  flex: 1;
  min-width: 250px;
  position: relative;

  .search-icon {
    position: absolute;
    left: 1rem;
    top: 50%;
    transform: translateY(-50%);
    color: #999;
  }

  input {
    width: 100%;
    padding: 0.75rem 1rem 0.75rem 2.5rem;
    border: 2px solid #e0e0e0;
    border-radius: 8px;
    font-size: 0.875rem;
    transition: border-color 0.3s ease;

    &:focus {
      outline: none;
      border-color: #2d5016;
    }
  }
`;

const MealsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 2rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }

  @media (max-width: 480px) {
    gap: 1rem;
  }
`;

const MealCard = styled(motion.div)`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-5px);
  }
`;

const MealImage = styled.div`
  height: 200px;
  background: linear-gradient(135deg, #2d5016, #4a7c59);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 3rem;
  position: relative;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
  }
`;

const MealInfo = styled.div`
  padding: 1.5rem;

  h3 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #2d5016;
    margin-bottom: 0.5rem;
  }

  .vendor-info {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: #666;
    font-size: 0.875rem;
    margin-bottom: 1rem;

    .vendor-name {
      font-weight: 500;
      color: #333;
    }
  }

  .description {
    color: #666;
    font-size: 0.875rem;
    line-height: 1.5;
    margin-bottom: 1rem;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .fulfillment-options {
    margin-bottom: 1rem;
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
  }

  .fulfillment-badge {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.25rem 0.75rem;
    border-radius: 20px;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;

    .icon {
      font-size: 0.875rem;
    }

    &.both {
      background: linear-gradient(135deg, #2d5016, #4a7c59);
      color: white;
    }

    &.delivery-only {
      background: #e8f5e8;
      color: #2d5016;
      border: 1px solid #2d5016;
    }

    &.pickup-only {
      background: #fff3e0;
      color: #e65100;
      border: 1px solid #e65100;
    }
  }

  .price-section {
    margin-bottom: 1rem;
    padding-top: 1rem;
    border-top: 1px solid #e0e0e0;

    .pricing-options {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
    }

    .price-option {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
    }

    .price-label {
      font-size: 0.875rem;
      font-weight: 600;
      color: #2d5016;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .price {
      font-size: 1.5rem;
      font-weight: 700;
      color: #2d5016;
    }

    .price-breakdown {
      font-size: 0.75rem;
      color: #666;
      font-style: italic;
    }

    .price-note {
      font-size: 0.75rem;
      color: #666;
    }
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 1rem;
`;

const ViewDetailsButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: 0.75rem;
  background: white;
  color: #2d5016;
  text-decoration: none;
  border: 2px solid #2d5016;
  border-radius: 6px;
  font-weight: 500;
  transition: all 0.3s ease;

  &:hover {
    background: #2d5016;
    color: white;
  }
`;

const OrderButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: 0.75rem;
  background: #2d5016;
  color: white;
  text-decoration: none;
  border-radius: 6px;
  font-weight: 500;
  transition: background 0.3s ease;

  &:hover {
    background: #1a3a0e;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: #666;

  h3 {
    margin-bottom: 1rem;
    color: #333;
  }
`;

const Meals = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [vendorFilter, setVendorFilter] = useState("");
  const [sortBy, setSortBy] = useState("name"); // name, price-asc, price-desc

  const { data, isLoading, error } = useQuery("meals", () => mealsAPI.getMeals(), {
    keepPreviousData: true,
  });

  const meals = data?.data?.data?.meals || [];

  // Get unique vendors for filter
  const vendors = [...new Set(meals.map((meal) => meal.business_name).filter(Boolean))];

  // Filter and sort meals
  let filteredMeals = meals.filter(
    (meal) =>
      meal.meal_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      meal.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      meal.business_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Apply vendor filter
  if (vendorFilter) {
    filteredMeals = filteredMeals.filter((meal) => meal.business_name === vendorFilter);
  }

  // Apply sorting
  filteredMeals = [...filteredMeals].sort((a, b) => {
    const basePriceA = parseFloat(a.base_price || 0);
    const basePriceB = parseFloat(b.base_price || 0);
    
    switch (sortBy) {
      case "price-asc":
        return basePriceA - basePriceB;
      case "price-desc":
        return basePriceB - basePriceA;
      case "name":
      default:
        return (a.meal_name || "").localeCompare(b.meal_name || "");
    }
  });

  if (isLoading) {
    return (
      <MealsContainer>
        <MealsContent>
          <PageHeader>
            <h1>Standalone Meals</h1>
            <p>Order individual meals from local vendors</p>
          </PageHeader>
          <LoadingSpinner />
        </MealsContent>
      </MealsContainer>
    );
  }

  if (error) {
    return (
      <MealsContainer>
        <MealsContent>
          <PageHeader>
            <h1>Standalone Meals</h1>
            <p>Order individual meals from local vendors</p>
          </PageHeader>
          <EmptyState>
            <h3>Error Loading Meals</h3>
            <p>Failed to load meals. Please try again.</p>
          </EmptyState>
        </MealsContent>
      </MealsContainer>
    );
  }

  return (
    <MealsContainer>
      <MealsContent>
        <PageHeader>
          <h1>Standalone Meals</h1>
          <p>Order individual meals from local vendors</p>
        </PageHeader>

        <SearchAndFilters>
          <FiltersRow>
            <SearchBar>
              <FiSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search meals, vendors..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </SearchBar>
            <div>
              <label style={{ display: "block", marginBottom: "0.5rem", fontSize: "0.875rem", fontWeight: 500, color: "#333" }}>
                Vendor:
              </label>
              <FilterSelect
                value={vendorFilter}
                onChange={(e) => setVendorFilter(e.target.value)}
              >
                <option value="">All Vendors</option>
                {vendors.map((vendor) => (
                  <option key={vendor} value={vendor}>
                    {vendor}
                  </option>
                ))}
              </FilterSelect>
            </div>
            <div>
              <label style={{ display: "block", marginBottom: "0.5rem", fontSize: "0.875rem", fontWeight: 500, color: "#333" }}>
                Sort by:
              </label>
              <FilterSelect
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="name">Name (A-Z)</option>
                <option value="price-asc">Price (Low to High)</option>
                <option value="price-desc">Price (High to Low)</option>
              </FilterSelect>
            </div>
          </FiltersRow>
        </SearchAndFilters>

        {filteredMeals.length === 0 ? (
          <EmptyState>
            <h3>No meals found</h3>
            <p>Try adjusting your search or check back later.</p>
          </EmptyState>
        ) : (
          <MealsGrid>
            {filteredMeals.map((meal, index) => {
              const basePrice = parseFloat(meal.base_price || 0);
              const deliveryFee = parseFloat(meal.delivery_fee || 0);
              const offersDelivery = meal.offers_delivery === true || meal.offers_delivery === "true" || meal.offers_delivery === 1;
              const offersPickup = meal.offers_pickup === true || meal.offers_pickup === "true" || meal.offers_pickup === 1;

              return (
                <MealCard
                  key={meal.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <MealImage>
                    {meal.image_url ? (
                      <img src={meal.image_url} alt={meal.meal_name} />
                    ) : (
                      <div className="placeholder">
                        <span>🍽️</span>
                      </div>
                    )}
                  </MealImage>
                  <MealInfo>
                    <h3>{meal.meal_name}</h3>

                    <div className="vendor-info">
                      <span>by</span>
                      <span className="vendor-name">{meal.business_name}</span>
                    </div>

                    {meal.description && (
                      <div className="description">{meal.description}</div>
                    )}

                    <div className="fulfillment-options">
                      {(() => {
                        if (offersDelivery && offersPickup) {
                          return (
                            <div className="fulfillment-badge both">
                              <FiTruck className="icon" />
                              <span>Delivery & Pickup</span>
                            </div>
                          );
                        } else if (offersDelivery && !offersPickup) {
                          return (
                            <div className="fulfillment-badge delivery-only">
                              <FiTruck className="icon" />
                              <span>Delivery Only</span>
                            </div>
                          );
                        } else if (!offersDelivery && offersPickup) {
                          return (
                            <div className="fulfillment-badge pickup-only">
                              <FiMapPin className="icon" />
                              <span>Pickup Only</span>
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </div>

                    <div className="price-section">
                      <div className="pricing-options">
                        {(() => {
                          if (offersDelivery) {
                            return (
                              <div className="price-option">
                                <div className="price-label">Delivery</div>
                                <div className="price">
                                  GYD ${Math.round(basePrice + deliveryFee).toLocaleString()}
                                </div>
                                {deliveryFee > 0 && (
                                  <div className="price-breakdown">
                                    Base: ${Math.round(basePrice).toLocaleString()}{" "}
                                    + Delivery: ${Math.round(deliveryFee).toLocaleString()}
                                  </div>
                                )}
                              </div>
                            );
                          }
                          if (offersPickup) {
                            return (
                              <div className="price-option">
                                <div className="price-label">Pickup</div>
                                <div className="price">
                                  GYD ${Math.round(basePrice).toLocaleString()}
                                </div>
                                <div className="price-note">
                                  Pickup from{" "}
                                  {meal.pickup_address || "vendor location"}
                                </div>
                              </div>
                            );
                          }
                          return (
                            <div className="price-option">
                              <div className="price">
                                GYD ${Math.round(basePrice).toLocaleString()}
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    </div>

                    <ActionButtons>
                      <ViewDetailsButton to={`/meals/${meal.id}`}>
                        View Details
                      </ViewDetailsButton>
                      <OrderButton
                        to={isAuthenticated ? `/meals/${meal.id}/order` : "/login"}
                      >
                        {isAuthenticated ? "Order Now" : "Login"}
                      </OrderButton>
                    </ActionButtons>
                  </MealInfo>
                </MealCard>
              );
            })}
          </MealsGrid>
        )}
      </MealsContent>
    </MealsContainer>
  );
};

export default Meals;

